import { useMotionValueEvent, useScroll, wrap } from "framer-motion";
import { graphql } from "gatsby";
import React, { FC, useCallback, useMemo, useRef } from "react";
import { useState } from "react";

import { useBreakpoints } from "../../../hooks/use-breakpoints";
import { MaterialsSliderDesktop } from "./materials-slider-desktop";
import { MaterialsSliderMobile } from "./materials-slider-mobile";
import { TabProps } from "./materials-slider-tabs";

export interface MaterialsSliderProps extends Queries.MaterialsSliderDataFragment {}

export const MaterialsSlider: FC<MaterialsSliderProps> = ({ showTabTitle, slides: _slides }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const { lg } = useBreakpoints();

  const slides = useMemo(() => Array.from(_slides ?? []), [_slides]);
  const [index, setSlide] = useState(0);

  const slideIndex = wrap(0, slides.length, index);

  const onChangeActiveTab = useCallback(
    (activeTabKey: string) => {
      const newIndex = slides.findIndex((slide) => slide?.id === activeTabKey);
      setSlide(newIndex);
    },
    [slides]
  );

  const onClickTab = useCallback(
    (activeTabKey: string) => {
      const newIndex = slides.findIndex((slide) => slide?.id === activeTabKey);
      const top = rootRef.current?.getBoundingClientRect().top ?? 0;
      window.scrollBy(0, top + window.innerHeight * newIndex);
    },
    [slides]
  );

  const tabs = useMemo<TabProps[]>(() => {
    return slides.filter(Boolean).map((slide) => ({
      key: slide!.id ?? "",
      label: slide!.title ?? "",
    }));
  }, [slides]);

  const { scrollYProgress } = useScroll({ target: rootRef });
  // we use a state to track the numeric value of scrollYProgress
  const [scrollYPercent, setScrollYPercent] = useState(-1);
  useMotionValueEvent(scrollYProgress, "change", (value) => {
    if (lg) {
      setScrollYPercent(value);
    }
  });

  return (
    <div
      style={{
        height: lg ? `${slides.length * 100}vh` : undefined,
        zIndex: 100,
      }}
      ref={rootRef}
    >
      <div className="sticky top-0 lg:h-screen">
        <MaterialsSliderDesktop
          {...{
            tabs,
            slides,
            onChangeActiveTab,
            onClickTab,
            slideIndex,
            scrollYPercent,
            showTabTitle,
          }}
        />
        <MaterialsSliderMobile {...{ tabs, slides, onChangeActiveTab, slideIndex, showTabTitle }} />
      </div>
    </div>
  );
};

export const query = graphql`
  fragment MaterialSlideData on ContentfulMaterialSlide {
    id
    title
    description {
      raw
    }
    image {
      gatsbyImageData
      url
    }
    cta {
      ...CtaData
    }
    variant
  }
  fragment MaterialsSliderData on ContentfulMaterialsSlider {
    showTabTitle
    slides {
      ...MaterialSlideData
    }
  }
`;
